import { useAttachmentUpload } from '@ophelos/ui/hooks/use-attachment-upload';
import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';
import { type ChatAttachmentDto, type ConnectAuthStage, uploadChatAttachmentToConnect } from '@/lib/connect-api';
import { useTranslation } from '@/hooks/useTranslation';

export function useChatAttachments(
  accountId: string | null,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
  dropZoneRef?: RefObject<HTMLElement | null>
) {
  const { t } = useTranslation();
  const upload = useCallback(
    (file: File) => uploadChatAttachmentToConnect(file, accountId as string, authStage, getAccessToken),
    [accountId, authStage, getAccessToken]
  );
  const messages = useMemo(
    () => ({
      maxAttachments: (max: number) => t('aiHelper.attachments.maxCount', { max }),
      fileTooLarge: t('aiHelper.attachments.fileTooLarge'),
      attachmentsTooLarge: t('aiHelper.attachments.tooLarge'),
      uploadFailed: t('aiHelper.attachments.uploadFailed'),
    }),
    [t]
  );
  const { items, addFiles, remove, clear, uploading, toAttachments, isDraggingOver, handlePaste } =
    useAttachmentUpload<ChatAttachmentDto>({
      upload,
      enabled: !!accountId,
      messages,
      dropZoneRef,
    });

  return {
    items,
    addFiles,
    remove,
    clear,
    uploading,
    toChatAttachments: toAttachments,
    isDraggingOver,
    handlePaste,
  };
}
